import { clearUserState, updateStationFilter } from "../slices/userSession/userSessionSlice";
import { clearAllTradeFilters as clearAllAdminTradeFilters } from "../slices/adminTrade/adminTradeSlice";
import { clearAllTradeFilters as clearAllEmployeeTradeFilters } from "../slices/employeeTrade/employeeTradeSlice";
import { clearAllAdminTimeOffRequestFilters } from "../slices/adminTimeOfRequest/adminTimeOfRequestSlice";
import { clearAllAdminTimeOffRequestWOPFilters } from "../slices/adminTimeOfRequest/adminTimeOffRequestWOPSlice";
import { clearAllTimeOffRequestState } from "../slices/timeOffRequest/timeOffRequestSlice";
import { clearAllWebsocketData } from "../slices/webSocketEvent/webSocketEventSlice";
import store from "../app/store";
import { clearAllTempAssignmentFilters } from "../slices/tempAssignment/tempAssignmentSlice";
import { clearTechOpsUserState } from "../slices/techOpsUserSession/techOpsUserSession";
import { clearAllAdminBidFilters } from "../slices/adminBid/adminBidSlice";
import { clearAllEmployeeBidFilters } from "../slices/employeeBid/employeeBidSlice";
import { clearCommonConfigState } from "../slices/commonConfig/commonConfigSlice";
import { SPECIAL_CHARS, SPECIAL_CHARS_WITH_SPACES } from "./constants";
import { clearAllEmployeeTimeOffRequestWOPFilters } from "../slices/timeOffRequest/employeeTimeOffRequestWOPSlice";
import { getWorkgroupGivenJobCode } from "./workgroupMapper";
import { getISODateTimeString } from "./dateTimeFormatter";
import { clearAllEmployeeVoluntaryOTFilters } from "../slices/employeeOvertime/employeeVoluntaryOTSlice";

export function sortOnCaseSensitiveTexts(a, b) {
  a = a.toLowerCase();
  b = b.toLowerCase();
  if (a === b) return 0;
  return a < b ? -1 : 1;
}

export const generateExportCsvButtonText = (selectionModel, selections) => {
  return `Download as CSV (${
    selectionModel.length === 0 || selectionModel.length === selections.length ? "ALL" : selectionModel.length
  } row${selectionModel.length !== 1 || selectionModel.length === selections.length ? "s" : ""})`;
};

export const getEmployeeStation = (featureConfigUserStations, userSession, stationFilter) => {
  // If you are temping and your currently selected station
  // does not have access to this feature, make sure we default to a valid station
  if (userSession.tempStation) {
    if (featureConfigUserStations.includes(stationFilter)) {
      if (stationFilter === userSession.activeStation || stationFilter === userSession.homeStation) {
        return stationFilter;
      } else {
        store.dispatch(updateStationFilter(userSession.activeStation));
        return userSession.activeStation;
      }
    } else {
      featureConfigUserStations.forEach((station) => {
        if (station === userSession.homeStation || station === userSession.activeStation) {
          store.dispatch(updateStationFilter(station));
          return station;
        }
      });
    }
  } else {
    store.dispatch(updateStationFilter(userSession.activeStation));
    return userSession.activeStation;
  }
};

export const getAdminStation = (featureConfigUserStations, userSession, stationFilter) => {
  // If you have multi-station access and your currently selected station
  // does not have access to this feature, make sure we default to a valid station.
  if (featureConfigUserStations.includes(stationFilter)) {
    if (stationFilter === userSession.homeStation) {
      if (userSession.multiStationAdminStationList.includes(stationFilter)) {
        return stationFilter;
      } else {
        store.dispatch(updateStationFilter(userSession.activeStation));
        return userSession.activeStation;
      }
    }
    return stationFilter;
  } else {
    userSession.multiStationAdminStationList.forEach((station) => {
      if (featureConfigUserStations.includes(station)) {
        store.dispatch(updateStationFilter(station));
        return station;
      }
    });
  }
};

export const getEmployeeStationList = (userSession) => {
  // If you are temping, employee screens will need access to both tempStation and homeStation,
  // otherwise you should only have access to your homeStation
  let stationList = [userSession.activeStation];
  if (userSession.tempStation) {
    stationList.push(userSession.homeStation);
  }
  return stationList;
};

export const getMultiStationAdminStationList = (userSession) => {
  // If your home station is not included in your multi-station admin station list
  // (because your home station in kronos was changed to something outside of this list)
  // we need to make sure that your new home station gets included in this list at runtime.
  const stationList = [...userSession.multiStationAdminStationList];
  if (!userSession.multiStationAdminStationList.includes(userSession.activeStation)) {
    stationList.push(userSession.activeStation);
  }
  return stationList;
};

export const isDuplicatePreference = (permanentBid, currentPreference) => {
  const result = permanentBid.find(
    (permanentBid) =>
      permanentBid.daysOff === currentPreference.daysOff &&
      permanentBid.position === currentPreference.position &&
      permanentBid.startTime === currentPreference.startTime
  );
  return !!result;
};
export const getFormattedPhoneNumber = (phoneNumber) => {
  const formattedPhoneNumber = phoneNumber.replace(/\D/g, "").replace(/(\d{3})(\d{3})(\d{4})/, "$1-$2-$3");
  return formattedPhoneNumber;
};

export const clearAllReduxState = () => {
  store.dispatch(clearUserState());
  store.dispatch(clearTechOpsUserState());
  store.dispatch(clearAllAdminTradeFilters());
  store.dispatch(clearAllEmployeeTradeFilters());
  store.dispatch(clearAllAdminTimeOffRequestFilters());
  store.dispatch(clearAllTimeOffRequestState());
  store.dispatch(clearAllAdminTimeOffRequestWOPFilters());
  store.dispatch(clearAllEmployeeTimeOffRequestWOPFilters());
  store.dispatch(clearAllTempAssignmentFilters());
  store.dispatch(clearAllAdminBidFilters());
  store.dispatch(clearAllEmployeeBidFilters());
  store.dispatch(clearAllWebsocketData());
  store.dispatch(clearCommonConfigState());
  store.dispatch(clearAllEmployeeVoluntaryOTFilters());
  window.localStorage.clear();
};

export const copyJsonObject = (object) => {
  return JSON.parse(JSON.stringify(object));
};
export const isObjectEmpty = (object) => {
  return Object.keys(object).length === 0;
};

export const compareArrays = (a, b) => a.length === b.length && a.every((element, index) => element === b[index]);

export const csvToJson = (headers, row) => {
  let jsonObj = {};
  for (let i = 0; i < headers.length; i++) {
    jsonObj[headers[i]] = row[i];
  }
  return jsonObj;
};

export const hasSpecialCharacters = (input) => {
  return [...input].some((char) => SPECIAL_CHARS.includes(char));
};

export const sanitizeSpecialChars = (input) => {
  let result = "";
  // replace special chars with underscore
  for (const char of input) {
    result += SPECIAL_CHARS.includes(char) ? "_" : char;
  }
  return result;
};

export const sanitizeSpecialCharsIncludingSpaces = (input) => {
  let result = "";
  // replace special chars with underscore
  for (const char of input) {
    result += SPECIAL_CHARS_WITH_SPACES.includes(char) ? "_" : char;
  }
  return result;
};

export const buildModifiedByFieldsFromUserSession = (userSession) => ({
  ...(userSession ? { modifiedByEid: userSession.eid } : {}),
  ...(userSession ? { modifiedByFirstName: userSession.firstName } : {}),
  ...(userSession ? { modifiedByLastName: userSession.lastName } : {}),
  ...(userSession ? { modifiedByWorkgroup: getWorkgroupGivenJobCode(userSession.jobCode) } : {}),
});

export const buildScheduleAndWindowDateFields = (bidData) => ({
  ...(bidData.schedStartDate ? { schedStartDate: getISODateTimeString(bidData.schedStartDate) } : {}),
  ...(bidData.schedEndDate ? { schedEndDate: getISODateTimeString(bidData.schedEndDate) } : {}),
  ...(bidData.windowEndDate ? { windowEndDate: getISODateTimeString(bidData.windowEndDate) } : {}),
  ...(bidData.windowStartDate ? { windowStartDate: getISODateTimeString(bidData.windowStartDate) } : {}),
  ...(bidData.schedMonth ? { schedMonth: bidData.schedMonth } : {}),
  ...(bidData.schedYear ? { schedYear: bidData.schedYear } : {}),
});
